import { useCallback, useRef, useState } from "react";

import { CaptureJob, CaptureProgress, CaptureResult } from "@components/ChartCapture/types";

/**
 * Bridges the imperative "await these captures" call to the declarative runner.
 * `capture(jobs)` sets the work list and resolves once the runner reports back;
 * the caller renders <OffscreenChartCaptureRunner> whenever `jobs` is non-null.
 */
export const useOffscreenCapture = () => {
  const [jobs, setJobs] = useState<CaptureJob[] | null>(null);
  const [progress, setProgress] = useState<CaptureProgress | null>(null);
  const resolverRef = useRef<((results: CaptureResult[]) => void) | null>(null);

  const capture = useCallback((next: CaptureJob[]): Promise<CaptureResult[]> => {
    if (next.length === 0) return Promise.resolve([]);
    setProgress({ done: 0, total: next.length });
    setJobs(next);
    return new Promise<CaptureResult[]>((resolve) => {
      resolverRef.current = resolve;
    });
  }, []);

  const handleProgress = useCallback((done: number, total: number) => setProgress({ done, total }), []);

  const handleComplete = useCallback((results: CaptureResult[]) => {
    setJobs(null);
    setProgress(null);
    const resolve = resolverRef.current;
    resolverRef.current = null;
    resolve?.(results);
  }, []);

  return { jobs, progress, capture, handleProgress, handleComplete };
};
